import {useState} from 'react';
import PropTypes from 'prop-types';
import {
  InfoLabel,
  Tab,
  TabsNavigation,
  TargetTypeLabel,
  Toggle
} from '@/components/atoms';
import {
  AccordionItem,
  ImageUploadField,
  PageHeader,
  TextInputField
} from '@/components/molecules';
import {
  DisplayContentTab,
  MediaPreviewWrapper,
  PageContainer
} from '@/components/organisms';
import {MEDIA_PREVIEW_BANNER} from '@/components/organisms/MediaPreview/mediaPreviewConfig';
import {DisplayPreReqContentTab} from '@/containers/DisplayPreReqContentTab/DisplayPreReqContentTab';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  setSalesTargetDisplayContentMedia,
  setSalesTargetDisplayContentMediaOptional
} from '@/store/slices/salesTargetSlice';
import {
  getSalesTargetDisplayContentConfig,
  SALES_TARGET_MEDIA_CONFIG
} from '@/utils/salesTargetDisplayContentConfig';
import {
  getSalesTargetBackgroundMediaSlot,
  getSalesTargetMediaSlot
} from '@/utils/salesTargetDisplayContentMedia';
import {IMAGETYPE} from '@/utils/constants/saleTargetConstants';
import {FONT_SIZE} from '@/utils/constants/globalStyle';
import './salesTargetDisplayContentV2.scss';

export const SalesTargetDisplayContentV2Layout = ({
  languages,
  displayPreReqContentFormData,
  setDisplayPreReqContentFormData,
  handleTabClickPreReq,
  displayContentFormData,
  setDisplayContentFormData,
  isPrerequisiteTarget
}) => {
  const [selectedTab, setSelectedTab] = useState('parent');
  const [mediaExpanded, setMediaExpanded] = useState(true);
  const salesTarget = useAppSelector((state) => state.salesTarget);
  const dispatch = useAppDispatch();
  const previewConfig = getSalesTargetDisplayContentConfig(
    salesTarget.targetType
  );
  const showBackgroundImage = Boolean(previewConfig.showBackgroundImage);
  const mediaSlot = getSalesTargetMediaSlot(salesTarget);
  const media = salesTarget[mediaSlot] || {};
  const backgroundSlot = showBackgroundImage
    ? getSalesTargetBackgroundMediaSlot(salesTarget)
    : null;
  const backgroundMedia = salesTarget[backgroundSlot] || {};
  const mediaConfig = previewConfig.media || SALES_TARGET_MEDIA_CONFIG;
  const backgroundMediaConfig = previewConfig.backgroundMedia || mediaConfig;
  const foregroundPreviewType =
    previewConfig.foregroundPreviewType || MEDIA_PREVIEW_BANNER;
  const backgroundPreviewType =
    previewConfig.backgroundPreviewType || MEDIA_PREVIEW_BANNER;
  const foregroundPreviewScale = previewConfig.foregroundPreviewScale || 0.3;
  const backgroundPreviewScale = previewConfig.backgroundPreviewScale || 0.3;
  const setSlotMedia = (slot, value) => {
    if (!slot) return;
    dispatch(
      (slot === 'displayContentMedia'
        ? setSalesTargetDisplayContentMedia
        : setSalesTargetDisplayContentMediaOptional)(value)
    );
  };
  const getMediaHandlers = (slot, slotMedia, imageId) => ({
    onChange: (image) =>
      setSlotMedia(slot, {
        ...slotMedia,
        title: image.fileName || '',
        imageId,
        imageUri: image.imageUrl,
        imageUrl: image.imageUrl,
        imageBase64: '',
        optIn: slotMedia.optIn ?? false
      }),
    onDelete: () =>
      setSlotMedia(slot, {
        ...slotMedia,
        title: '',
        imageUri: '',
        imageUrl: '',
        imageBase64: '',
        imageId
      }),
    onTitleChange: (title) => setSlotMedia(slot, {...slotMedia, title}),
    onOptInChange: (optIn) => setSlotMedia(slot, {...slotMedia, optIn})
  });
  const getMediaDescription = (config) => {
    const description = [
      config.acceptedTypesLabel ||
        config.acceptedTypes
          .map((type) => type.split('/')[1].toUpperCase())
          .join(', '),
      config.dimensions &&
        `${config.dimensions.width} × ${config.dimensions.height} px`,
      config.colorMode,
      `Max. ${config.maxSizeBytes / 1024} KB`
    ]
      .filter(Boolean)
      .join(' • ');
    return config.descriptionPrefix
      ? `${config.descriptionPrefix} ${description}`
      : description;
  };
  const getMediaImage = (item) => item?.imageUrl || item?.imageUri || '';
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    handleTabClickPreReq(tab);
  };
  const handleFormChange = (data) =>
    setDisplayPreReqContentFormData((prev) =>
      selectedTab === 'parent'
        ? {...prev, ...data}
        : {...prev, child: {...prev.child, ...data}}
    );
  const previewImage = getMediaImage(media);
  const backgroundImage = getMediaImage(backgroundMedia);
  const extraMedia =
    mediaSlot === 'displayContentMedia'
      ? salesTarget.displayContentMediaOptional
      : salesTarget.displayContentMedia;
  const layeredBackgroundImage =
    !showBackgroundImage && extraMedia?.imageId === IMAGETYPE.Background
      ? getMediaImage(extraMedia) || undefined
      : undefined;

  const renderImageSection = ({
    slot,
    slotMedia,
    imageId,
    heading,
    titleLabel,
    config,
    previewType,
    previewScale,
    previewContent,
    testIdPrefix,
    noSlotMessage,
    className = ''
  }) => {
    const description = getMediaDescription(config);
    const handlers = getMediaHandlers(slot, slotMedia, imageId);
    const image = getMediaImage(slotMedia);
    return (
      <div
        data-testid={`${testIdPrefix}-section`}
        className={`sales-target-v2-banner w-full rounded-lg border border-[color:var(--color-border-default)] p-5 ${className}`.trim()}>
        <h3 className='mb-4 text-sm font-bold'>{heading}</h3>
        <div className='grid gap-6 md:grid-cols-2'>
          <div className='sales-target-v2-upload min-w-0'>
            <ImageUploadField
              label='Upload Image'
              tooltipText={description}
              description={description}
              imageUri={image}
              acceptedTypes={config.acceptedTypes}
              maxSizeBytes={config.maxSizeBytes}
              dimensions={config.dimensions}
              aspectRatio={config.aspectRatio}
              aspectRatioLabel={config.aspectRatioLabel}
              validateDimensions={Boolean(config.dimensions)}
              validateAspectRatio={Boolean(config.aspectRatio)}
              onChange={handlers.onChange}
              onDelete={handlers.onDelete}
              disabled={!slot}
              dataTestId={`${testIdPrefix}-upload`}
              containerClassName='w-full'
            />
            {!slot && <p role='alert'>{noSlotMessage}</p>}
            <div className='sales-target-v2-warnings mb-4 mt-2 flex flex-wrap gap-3'>
              <InfoLabel title='Avoid white backgrounds' />
              <InfoLabel title='No text in the image' />
            </div>
            <div className='sales-target-v2-media-fields flex flex-wrap items-end gap-6'>
              <TextInputField
                label={titleLabel}
                value={slotMedia.title || ''}
                schema={{type: 'string'}}
                uiSchema={{
                  label: titleLabel,
                  placeholder: 'Filename.png',
                  isDark: true
                }}
                updateValue={handlers.onTitleChange}
                visible
              />
              <Toggle
                id={`${testIdPrefix}-opt-in`}
                value={Boolean(slotMedia.optIn)}
                updateValue={handlers.onOptInChange}
                uiSchema={{label: 'Opt in', isDark: true, size: 'small'}}
              />
            </div>
          </div>
          <div className='min-w-0 md:border-l md:border-[color:var(--color-border-default)] md:pl-6'>
            <p className='mb-4 text-sm'>Image Preview in App</p>
            <MediaPreviewWrapper
              types={[previewType]}
              scale={previewScale}
              content={[previewContent]}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {isPrerequisiteTarget && (
        <TabsNavigation
          activeTabIndex={selectedTab === 'parent' ? 0 : 1}
          className='w-full border-b border-[color:var(--color-border-divider)]'>
          {['parent', 'child'].map((tab) => (
            <Tab
              key={tab}
              label={tab === 'parent' ? 'Parent' : 'Child'}
              onClick={() => handleTabChange(tab)}
              isDark
            />
          ))}
        </TabsNavigation>
      )}
      <PageContainer
        data-testid='sales-target-v2-media'
        className='sales-target-v2-media shadow pc-block mt-4 flex w-full flex-col rounded-lg p-4 text-white'>
        <AccordionItem
          presentation='no-background'
          isDark
          expanded={mediaExpanded}
          index={0}
          label='Media'
          onUpdate={() => setMediaExpanded((prev) => !prev)}>
          {renderImageSection({
            slot: mediaSlot,
            slotMedia: media,
            imageId: IMAGETYPE.Foreground,
            heading: showBackgroundImage
              ? 'Foreground Image*'
              : 'Target Banner*',
            titleLabel: showBackgroundImage
              ? 'Foreground Image Title'
              : 'Image title',
            config: mediaConfig,
            previewType: foregroundPreviewType,
            previewScale: foregroundPreviewScale,
            previewContent: {
              image: previewImage || undefined,
              backgroundImage: layeredBackgroundImage
            },
            testIdPrefix: 'sales-target-v2',
            noSlotMessage:
              'Existing media has no available foreground slot. Update the existing media before uploading a banner.',
            className: 'mt-6'
          })}
          {showBackgroundImage &&
            renderImageSection({
              slot: backgroundSlot,
              slotMedia: backgroundMedia,
              imageId: IMAGETYPE.Background,
              heading: 'Background Image*',
              titleLabel: 'Background Image Title',
              config: backgroundMediaConfig,
              previewType: backgroundPreviewType,
              previewScale: backgroundPreviewScale,
              previewContent: {image: backgroundImage || undefined},
              testIdPrefix: 'sales-target-v2-bg',
              noSlotMessage:
                'Existing media has no available background slot. Update the existing media before uploading a background image.',
              className: 'mt-6'
            })}
        </AccordionItem>
      </PageContainer>
      <PageContainer
        data-testid='sales-target-v2-display-content'
        className='sales-target-v2-information shadow pc-block mb-3 mt-4 flex w-full flex-col rounded-lg p-4 text-white'>
        <PageHeader
          title='Display Information'
          fontSize={FONT_SIZE.LG}
          wrapperClassName='mb-4 border-b border-[color:var(--color-border-divider)] pb-4'
        />
        <TargetTypeLabel value={salesTarget.targetType} />
        {isPrerequisiteTarget ? (
          <DisplayPreReqContentTab
            key={selectedTab}
            formData={
              selectedTab === 'parent'
                ? displayPreReqContentFormData
                : displayPreReqContentFormData.child || {}
            }
            setFormData={handleFormChange}
            languages={languages}
            showTargetType={false}
            targetType={salesTarget.targetType}
          />
        ) : (
          <DisplayContentTab
            displayContentFormData={displayContentFormData}
            setDisplayContentFormData={setDisplayContentFormData}
            languages={languages}
            targetType={salesTarget.targetType}
            showAllLanguages={true}
          />
        )}
      </PageContainer>
    </>
  );
};

SalesTargetDisplayContentV2Layout.propTypes = {
  languages: PropTypes.array.isRequired,
  displayPreReqContentFormData: PropTypes.object.isRequired,
  setDisplayPreReqContentFormData: PropTypes.func.isRequired,
  handleTabClickPreReq: PropTypes.func.isRequired,
  displayContentFormData: PropTypes.object.isRequired,
  setDisplayContentFormData: PropTypes.func.isRequired,
  isPrerequisiteTarget: PropTypes.bool.isRequired
};
